import React from 'react';
import { MessageSquare } from 'lucide-react';
import type { Theme } from '../types';
import { resolveThemeToken, isColorToken } from '../utils/theme';
import type { WidgetStrings } from '../utils/i18n';
import { formatWidgetString, DEFAULT_WIDGET_STRINGS } from '../utils/i18n';

interface MinimizedButtonProps {
  unreadCount: number;
  theme: Theme;
  onClick: () => void;
  controlsId?: string;
  strings?: WidgetStrings;
}

const MinimizedButton = React.memo(function MinimizedButton({
  unreadCount,
  theme,
  onClick,
  controlsId,
  strings = DEFAULT_WIDGET_STRINGS,
}: MinimizedButtonProps) {
  const buttonBackground = resolveThemeToken(theme.primary, 'backgroundColor');
  const iconText = resolveThemeToken(theme.text, 'color');
  const iconWrap = resolveThemeToken(theme.accent, 'backgroundColor');
  const hoverClass = isColorToken(theme.hover) ? '' : theme.hover;
  const label =
    unreadCount > 0
      ? formatWidgetString(strings.openChatUnread, {
          count: unreadCount,
          plural: unreadCount === 1 ? '' : 's',
        })
      : strings.openChat;
  const unreadBadgeText = unreadCount > 99 ? '99+' : String(unreadCount);
  return (
    <button
      onClick={onClick}
      type="button"
      aria-label={label}
      aria-controls={controlsId}
      aria-haspopup="dialog"
      className={`relative h-14 w-14 inline-flex items-center justify-center rounded-full shadow-lg border border-white/20 ${buttonBackground.className} ${hoverClass} focus:outline-2 focus:outline-blue-500 transition-transform duration-150`}
      style={{ ...buttonBackground.style }}
    >
      <span
        className={`h-8 w-8 rounded-full inline-flex items-center justify-center ${iconWrap.className}`}
        style={{ ...iconWrap.style }}
      >
        <MessageSquare className="w-5 h-5" aria-hidden="true" style={iconText.style} />
      </span>
      {unreadCount > 0 && (
        <span
          className="absolute -top-1 -right-1 h-5 px-1 inline-flex items-center justify-center rounded-full bg-red-600 text-white text-[10px] font-semibold border border-white shadow-sm"
          style={{ minWidth: '1.25rem' }}
          aria-hidden="true"
        >
          {unreadBadgeText}
        </span>
      )}
    </button>
  );
});

export default MinimizedButton;
